import React, { useEffect, useState } from "react";
import { FaDownload } from "react-icons/fa";
import { Link } from "react-router-dom";
import api from "../../services/api";
import { groupPeriodsByUser, resolveMonthlyCost } from "../../utils/user_contract";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";

export default function Profitability({ user, filters: parentFilters, organisation, businessUnit }) {
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const now = new Date();
    return { type: "month", year: now.getFullYear(), month: now.getMonth() };
  });
  const [localFilters, setLocalFilters] = useState({
    toInvoice: false,
    hideNonBillable: true,
  });
  const [profitabilityData, setProfitabilityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const u = useSelector((state) => state.Auth.user);

  useEffect(() => {
    if (!u && !user) return;
    fetchProfitabilityData();
  }, [user, selectedPeriod, localFilters, parentFilters, localFilters.hideNonBillable, businessUnit]);

  const calculateWorkingPeriod = (user, dateRange) => {
    const periodStart = dateRange.startDate;
    const periodEnd = dateRange.endDate;

    let actualStart = periodStart;
    if (user.starting_date) {
      const userStartDate = new Date(user.starting_date);
      if (userStartDate > periodStart) {
        actualStart = userStartDate;
      }
    }

    let actualEnd = periodEnd;
    if (user.exit_date) {
      const userExitDate = new Date(user.exit_date);
      if (userExitDate < periodEnd) {
        actualEnd = userExitDate;
      }
    }

    if (actualStart > periodEnd || actualEnd < periodStart) {
      return {
        monthsFraction: 0,
        description: "Not active in period",
        actualStart,
        actualEnd,
        daysWorked: 0,
      };
    }

    const totalDays = Math.ceil((actualEnd - actualStart) / (1000 * 60 * 60 * 24)) + 1;
    const monthsFraction = totalDays / 30.44;

    let description = "Full period";
    if (user.starting_date && new Date(user.starting_date) > periodStart) {
      description = `Started ${actualStart.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" })}`;
    }
    if (user.exit_date && new Date(user.exit_date) < periodEnd) {
      description = `Left ${actualEnd.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" })}`;
    }
    if (user.starting_date && user.exit_date && new Date(user.starting_date) > periodStart && new Date(user.exit_date) < periodEnd) {
      description = `${actualStart.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" })} - ${actualEnd.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
      })}`;
    }

    return {
      monthsFraction: Math.max(0, monthsFraction),
      description,
      actualStart,
      actualEnd,
      daysWorked: totalDays,
    };
  };

  // Calculate date range based on selected period (month, quarter, or year)
  const getDateRange = () => {
    const { type, year, month, quarter, custom } = selectedPeriod;
    
    if (type === "custom" && custom) {
      return {
        startDate: new Date(Date.UTC(custom.startYear, custom.startMonth, 1)),
        endDate: new Date(Date.UTC(custom.endYear, custom.endMonth + 1, 0)),
      };
    } else if (type === "month") {
      return {
        startDate: new Date(Date.UTC(year, month, 1)),
        endDate: new Date(Date.UTC(year, month + 1, 0)),
      };
    } else if (type === "quarter") {
      const quarterStartMonth = (quarter - 1) * 3;
      return {
        startDate: new Date(Date.UTC(year, quarterStartMonth, 1)),
        endDate: new Date(Date.UTC(year, quarterStartMonth + 3, 0)),
      };
    } else if (type === "year") {
      return {
        startDate: new Date(Date.UTC(year, 0, 1)),
        endDate: new Date(Date.UTC(year, 11, 31)),
      };
    }
  };

  const fetchProfitabilityData = async () => {
    try {
      setLoading(true);

      // Calculate date range based on period type
      const dateRange = getDateRange();

      // Fetch team members
      const userQuery = {
        role: ["admin", "user", "super_admin"],
        contracts: ["VIE", "CDD", "CDI", "APPRENTICESHIP", "INTERNSHIP", "FREELANCE", "Me kohe te plote"],
      };

      // If a manager is selected (from parent), filter by manager
      if (user) {
        userQuery.manager_id = user._id;
        userQuery.managees_of_managees = parentFilters?.manageesOfManagees || false;
      }
      
      // Filter by business unit
      if (businessUnit._id !== "ALL") {
        userQuery.business_unit_id = businessUnit._id;
      } else if (organisation?._id && organisation._id !== "ALL") {
        userQuery.organisation_id = organisation._id;
      }

      const { data: activeUsersData } = await api.post(`/user/search`, {
        ...userQuery,
        status: "active",
      });

      const { data: inactiveUsersData } = await api.post(`/user/search`, {
        ...userQuery,
        status: "inactive",
      });

      let allPotentialMembers = [...activeUsersData.users, ...inactiveUsersData.users];
      
      // If a manager is selected, ensure they're included in the list
      if (user) {
        const managerAlreadyIncluded = allPotentialMembers.some((member) => member._id === user._id);
        if (!managerAlreadyIncluded) {
          allPotentialMembers.push(user);
        }
      }
      
      // Apply same filtering as TACE view for consistency
      const today = new Date();
      allPotentialMembers = allPotentialMembers.filter((u) => {
        // Exclude support functions based on toggle
        if (localFilters.hideNonBillable && u.non_billable) return false;
        // Exclude daily freelancers (they're billed per project, not tracked same way)
        if (u.contract === "FREELANCE" && u.payment_type === "DAILY") return false;
        // Exclude users who haven't started yet
        if (u.starting_date && new Date(u.starting_date) > today) return false;
        return true;
      });

      // Fetch activities for the entire month range
      const activityQuery = {
        userIds: allPotentialMembers.map((u) => u._id),
        dateFrom: dateRange.startDate.getTime(),
        dateTo: dateRange.endDate.getTime(),
      };

      const { data: activitiesData } = await api.post(`/activity/search`, activityQuery);
      const validActivities = activitiesData.filter((activity) => activity.projectName !== "Unavailable" && activity.budgetName !== "Unavailable");

      // Dated compensation periods → resolve each member's monthly cost as of the period end.
      const { data: contractsData } = await api.post("/user_contract/search", businessUnit._id !== "ALL" ? { business_unit_id: businessUnit._id, limit: 10000 } : { limit: 10000 });
      const periodsByUser = groupPeriodsByUser(contractsData || []);

      // Define leave projects
      const leaveProjects = ["Leave Paid", "RTT", "Leave Unpaid", "Arret Maladie", "Holiday", "Holliday", "Unavailable"];
      const workActivities = validActivities.filter((activity) => !leaveProjects.includes(activity.projectName));

      // Get unique invoice IDs
      const allInvoiceIds = Array.from(new Set(workActivities.filter((a) => a.invoiceId).map((a) => a.invoiceId)));

      // Fetch invoices
      let invoicesData = [];
      if (allInvoiceIds.length > 0) {
        try {
          const invoicesPromises = allInvoiceIds.map((invoiceId) => api.get(`/invoice/${invoiceId}`).catch(() => ({ ok: false })));
          const invoicesResults = await Promise.all(invoicesPromises);
          invoicesData = invoicesResults.filter((result) => result.ok && result.data).map((result) => result.data);
        } catch (error) {
          console.error("Error fetching invoices:", error);
        }
      }

      const invoiceMap = new Map();
      invoicesData.forEach((invoice) => {
        invoiceMap.set(invoice._id, invoice);
      });

      // Fetch ALL activities for each invoice (without date restriction) to calculate correct proportions
      const invoiceActivitiesMap = new Map();
      if (allInvoiceIds.length > 0) {
        try {
          const allInvoiceActivitiesPromises = allInvoiceIds.map((invoiceId) => 
            api.post(`/activity/search`, { invoiceId }).catch(() => ({ ok: false, data: [] }))
          );
          const allInvoiceActivitiesResults = await Promise.all(allInvoiceActivitiesPromises);
          allInvoiceActivitiesResults.forEach((result, idx) => {
            if (result.ok && result.data) {
              const invoiceId = allInvoiceIds[idx];
              invoiceActivitiesMap.set(invoiceId, result.data);
            }
          });
        } catch (error) {
          console.error("Error fetching invoice activities:", error);
        }
      }

      // Calculate person-level profitability
      const personProfitability = [];
      const relevantTeamMembers = allPotentialMembers.filter((member) => {
        const workingPeriod = calculateWorkingPeriod(member, dateRange);
        const hasActivities = validActivities.some((activity) => activity.userId === member._id);
        if (member.status === "inactive") {
          return hasActivities;
        }
        return workingPeriod.monthsFraction > 0 || hasActivities;
      });

      relevantTeamMembers.forEach((member) => {
        const memberActivities = workActivities.filter((activity) => activity.userId === member._id);
        if (memberActivities.length === 0) return;

        // Calculate member costs
        let totalMemberCost = 0;
        const baseTjms = member.tjms || member.user_tjms || 0;

        if (member.payment_type === "MONTHLY") {
          const baseMonthlyCost = resolveMonthlyCost(periodsByUser, member, dateRange.endDate) || member.payment_value || 0;
          const actualWorkingPeriod = calculateWorkingPeriod(member, dateRange);
          const monthsFraction = actualWorkingPeriod.monthsFraction;
          totalMemberCost = baseMonthlyCost * monthsFraction;
        } else {
          totalMemberCost = memberActivities.reduce((acc, activity) => acc + (activity.cost || 0), 0);
        }

        // Group by project
        const projectBreakdown = new Map();
        memberActivities.forEach((activity) => {
          const projectKey = activity.projectId || activity.projectName || "Unknown";
          const projectName = activity.projectName || "Unknown Project";

          if (!projectBreakdown.has(projectKey)) {
            projectBreakdown.set(projectKey, {
              projectId: activity.projectId || "",
              projectName: projectName,
              projectType: activity.projectType || "",
              hours: 0,
              daysWorked: 0,
              calculatedRevenue: 0,
              invoicedRevenue: 0,
              activities: [],
            });
          }

          const project = projectBreakdown.get(projectKey);
          const activityHours = activity.total || 0;
          const activityDays = activityHours / 8;
          project.hours += activityHours;
          project.daysWorked += activityDays;
          project.calculatedRevenue += activity.value || 0;
          // Store projectType - prioritize ADMIN/TIMEOFF if found
          if (activity.projectType) {
            const actProjectType = (activity.projectType || "").toUpperCase();
            if (actProjectType === "ADMIN" || actProjectType === "TIMEOFF") {
              project.projectType = activity.projectType; // Keep original case
            } else if (!project.projectType) {
              project.projectType = activity.projectType;
            }
          }
          project.activities.push(activity);
        });

        // Calculate total hours for cost allocation
        const totalMemberHours = memberActivities.reduce((acc, activity) => acc + (activity.total || 0), 0);

        // Allocate costs and calculate invoiced revenue
        const projectAllocations = [];
        projectBreakdown.forEach((project) => {
          let allocatedCost = 0;
          if (member.payment_type === "MONTHLY" && totalMemberHours > 0) {
            allocatedCost = (project.hours / totalMemberHours) * totalMemberCost;
          } else {
            allocatedCost = project.activities.reduce((acc, activity) => acc + (activity.cost || 0), 0);
          }

          // Calculate invoiced revenue
          let invoicedRevenue = 0;
          const uniqueInvoiceIds = new Set();
          project.activities.forEach((activity) => {
            if (activity.invoiceId) uniqueInvoiceIds.add(activity.invoiceId);
          });

          uniqueInvoiceIds.forEach((invoiceId) => {
            const invoice = invoiceMap.get(invoiceId);
            if (invoice && invoice.total) {
              // Use ALL activities from the invoice (across all months) to calculate the correct proportion
              const allInvoiceActivities = invoiceActivitiesMap.get(invoiceId) || [];
              const invoiceTotalValue = allInvoiceActivities.reduce((sum, a) => sum + (a.value || 0), 0);
              
              // Only use activities from this person's project in the selected month
              const personProjectInvoiceActivities = project.activities.filter((a) => a.invoiceId === invoiceId);
              const personProjectInvoiceValue = personProjectInvoiceActivities.reduce((sum, a) => sum + (a.value || 0), 0);

              if (invoiceTotalValue > 0 && invoiceTotalValue >= invoice.total * 0.9) {
                // Only allocate if invoice total is close to the sum of activity values (within 10%)
                // This ensures we're not allocating incorrectly when invoice totals don't match activity values
                const personShare = (personProjectInvoiceValue / invoiceTotalValue) * invoice.total;
                invoicedRevenue += personShare;
              } else if (invoiceTotalValue > 0) {
                // If invoice total doesn't match activity values, still allocate
                // The invoice might have manual adjustments
                const personShare = (personProjectInvoiceValue / invoiceTotalValue) * invoice.total;
                invoicedRevenue += personShare;
              }
            }
          });

          // Calculate overwritten TJM
          // Set TJM to 0 for admin projects or leave projects
          // Check for variations of admin project names (case-insensitive, partial matches)
          const adminProjectNames = ["accounting", "selego", "admin"];
          const leaveProjectNames = ["leave paid", "rtt", "leave unpaid", "arret maladie", "holiday", "holliday", "timeoff"];
          
          const projectNameLower = (project.projectName || "").toLowerCase().trim();
          const projectTypeLower = (project.projectType || "").toLowerCase();
          
          // Check if project is admin by name, projectType (case-insensitive), or budget type
          const allActivitiesAreAdmin = project.activities.length > 0 && 
            project.activities.every((act) => (act.budgetType || "").toLowerCase() === "admin");
          const anyActivityIsAdmin = project.activities.some((act) => (act.budgetType || "").toLowerCase() === "admin");
          const anyActivityProjectTypeIsAdmin = project.activities.some((act) => {
            const actProjectType = (act.projectType || "").toLowerCase();
            return actProjectType === "admin";
          });
          const projectTypeIsAdmin = projectTypeLower === "admin";
          // Check if project name contains any admin keywords
          const projectNameIsAdmin = adminProjectNames.some(adminName => 
            projectNameLower.includes(adminName) || projectNameLower === adminName
          );
          const anyActivityProjectNameIsAdmin = project.activities.some((act) => {
            const actProjectNameLower = (act.projectName || "").toLowerCase().trim();
            return adminProjectNames.some(adminName => 
              actProjectNameLower.includes(adminName) || actProjectNameLower === adminName
            );
          });
          
          const isAdminProject = projectTypeIsAdmin || projectNameIsAdmin || anyActivityProjectNameIsAdmin || allActivitiesAreAdmin || anyActivityIsAdmin || anyActivityProjectTypeIsAdmin;
          
          // Check if project is leave/timeoff by name or projectType (case-insensitive)
          const projectTypeIsTimeoff = projectTypeLower === "timeoff";
          const anyActivityProjectTypeIsTimeoff = project.activities.some((act) => {
            const actProjectType = (act.projectType || "").toLowerCase();
            return actProjectType === "timeoff";
          });
          
          const isLeaveProject = 
            projectTypeIsTimeoff ||
            anyActivityProjectTypeIsTimeoff ||
            leaveProjectNames.includes(projectNameLower) ||
            project.activities.some((act) => 
              leaveProjectNames.includes((act.projectName || "").toLowerCase().trim())
            );
          
          const shouldSetTjmToZero = isAdminProject || isLeaveProject;
          
          // ALWAYS set to 0 if admin/timeoff, regardless of what activities say
          let avgOverwrittenTjms;
          if (shouldSetTjmToZero) {
            avgOverwrittenTjms = 0;
          } else {
            // Calculate weighted average of overwritten TJMs
            const tjmsSum = project.activities.reduce((sum, act) => {
              const actTjms = act.userTjms || act.user_tjms || baseTjms;
              const actDays = (act.total || 0) / 8;
              return sum + (actTjms * actDays);
            }, 0);
            avgOverwrittenTjms = project.daysWorked > 0 ? tjmsSum / project.daysWorked : baseTjms;
          }
          
          // Final verification log for admin projects
          if (projectNameLower.includes("selego") || projectNameLower.includes("accounting") || projectTypeLower === "admin") {
            console.log("🔍 FINAL TJM Value for", project.projectName, ":", {
              personName: member.name,
              shouldSetTjmToZero,
              avgOverwrittenTjms,
              storedValue: avgOverwrittenTjms,
              projectType: project.projectType,
              projectTypeLower,
              isAdminProject,
              isLeaveProject,
            });
          }

          // Ensure overwrittenTjms is explicitly 0 for admin/timeoff projects
          const finalOverwrittenTjms = shouldSetTjmToZero ? 0 : avgOverwrittenTjms;
          
          // For admin/timeoff projects, set revenue to 0 (they are cost centers, not revenue generators)
          const finalCalculatedRevenue = shouldSetTjmToZero ? 0 : project.calculatedRevenue;
          const finalInvoicedRevenue = shouldSetTjmToZero ? 0 : invoicedRevenue;
          
          // Calculate benefit and margin with zero revenue for admin/timeoff projects
          const finalBenefitFromInvoiced = finalInvoicedRevenue - allocatedCost;
          const finalBenefitFromInvoicable = finalCalculatedRevenue - allocatedCost;
          const finalProjectBenefit = finalInvoicedRevenue > 0 ? finalBenefitFromInvoiced : finalBenefitFromInvoicable;
          
          // Calculate margin based on the revenue used for benefit calculation
          // For admin/timeoff projects (zero revenue), margin is 0
          const finalRevenueForMargin = finalInvoicedRevenue > 0 ? finalInvoicedRevenue : finalCalculatedRevenue;
          const finalProjectMargin = finalRevenueForMargin > 0 ? (finalProjectBenefit / finalRevenueForMargin) * 100 : 0;
          
          projectAllocations.push({
            projectId: project.projectId,
            projectName: project.projectName,
            projectType: project.projectType || "",
            hours: project.hours,
            daysWorked: project.daysWorked,
            allocatedCost: allocatedCost,
            calculatedRevenue: finalCalculatedRevenue,
            invoicedRevenue: finalInvoicedRevenue,
            invoiceIds: Array.from(uniqueInvoiceIds),
            baseTjms: baseTjms,
            overwrittenTjms: finalOverwrittenTjms,
            benefit: finalProjectBenefit,
            benefitFromInvoiced: finalBenefitFromInvoiced,
            benefitFromInvoicable: finalBenefitFromInvoicable,
            margin: finalProjectMargin,
            marginFromInvoiced: finalInvoicedRevenue > 0 ? (finalBenefitFromInvoiced / finalInvoicedRevenue) * 100 : 0,
            marginFromInvoicable: finalCalculatedRevenue > 0 ? (finalBenefitFromInvoicable / finalCalculatedRevenue) * 100 : 0,
          });
        });

        projectAllocations.sort((a, b) => b.calculatedRevenue - a.calculatedRevenue);

        personProfitability.push({
          userId: member._id,
          name: member.name,
          baseTjms: baseTjms,
          totalCost: totalMemberCost,
          projectBreakdown: projectAllocations,
        });
      });

      personProfitability.sort((a, b) => {
        const aTotal = a.projectBreakdown.reduce((sum, p) => sum + p.calculatedRevenue, 0);
        const bTotal = b.projectBreakdown.reduce((sum, p) => sum + p.calculatedRevenue, 0);
        return bTotal - aTotal;
      });

      setProfitabilityData({
        personProfitability,
        dateRange,
      });
    } catch (error) {
      console.error(error);
      toast.error("Error fetching profitability data");
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!profitabilityData || !profitabilityData.personProfitability || profitabilityData.personProfitability.length === 0) {
      toast.error("No data to export");
      return;
    }

    // Generate label for filename based on period type
    const { type, year, month, quarter, custom } = selectedPeriod;
    let periodLabel;
    if (type === "custom" && custom) {
      periodLabel = `sept-dec-${custom.endYear}`;
    } else if (type === "month") {
      const date = new Date(year, month, 1);
      periodLabel = date.toLocaleString("default", { month: "long", year: "numeric" });
    } else if (type === "quarter") {
      periodLabel = `Q${quarter}-${year}`;
    } else if (type === "year") {
      periodLabel = `${year}-full-year`;
    }
    const csvRows = [];

    // Main table
    csvRows.push(["Person", "Project", "Type", "Cost €", "TJM Sell €", "TJM Overwritten €", "Days Worked", "Invoicable €", "Invoiced €", "Benefit €", "Margin %"]);

    profitabilityData.personProfitability.forEach((person) => {
      person.projectBreakdown.forEach((project) => {
        csvRows.push([
          person.name,
          project.projectName,
          project.projectType || "",
          Math.round(project.allocatedCost),
          Math.round(person.baseTjms || 0),
          Math.round(project.overwrittenTjms ?? person.baseTjms ?? 0),
          project.daysWorked.toFixed(1),
          Math.round(project.calculatedRevenue),
          Math.round(project.invoicedRevenue || 0),
          Math.round(project.benefit), // Uses invoiced if available, otherwise invoicable
          project.margin.toFixed(1), // Uses invoiced if available, otherwise invoicable
        ]);
      });
    });

    // Add totals row
    const totalCost = profitabilityData.personProfitability.reduce((sum, person) => 
      sum + person.projectBreakdown.reduce((pSum, project) => pSum + project.allocatedCost, 0), 0
    );
    const totalDays = profitabilityData.personProfitability.reduce((sum, person) => 
      sum + person.projectBreakdown.reduce((pSum, project) => pSum + project.daysWorked, 0), 0
    );
    const totalInvoicable = profitabilityData.personProfitability.reduce((sum, person) => 
      sum + person.projectBreakdown.reduce((pSum, project) => pSum + project.calculatedRevenue, 0), 0
    );
    const totalInvoiced = profitabilityData.personProfitability.reduce((sum, person) => 
      sum + person.projectBreakdown.reduce((pSum, project) => pSum + project.invoicedRevenue, 0), 0
    );
    const totalBenefit = profitabilityData.personProfitability.reduce((sum, person) => 
      sum + person.projectBreakdown.reduce((pSum, project) => pSum + project.benefit, 0), 0
    );
    const totalMargin = totalInvoicable > 0 ? (totalBenefit / totalInvoicable) * 100 : 0;

    csvRows.push([
      "TOTAL",
      "",
      "",
      Math.round(totalCost),
      "",
      "",
      totalDays.toFixed(1),
      Math.round(totalInvoicable),
      Math.round(totalInvoiced),
      Math.round(totalBenefit),
      totalMargin.toFixed(1),
    ]);

    const csvContent = csvRows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `profitability-${periodLabel.replace(/\s+/g, "-").toLowerCase()}-${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("CSV exported successfully");
  };

  const getPeriodOptions = () => {
    const options = [];
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const lastYear = currentYear - 1;
    
    // Add yearly options (current year and last year)
    options.push({
      value: JSON.stringify({ type: "year", year: currentYear }),
      label: `📅 ${currentYear} (Full Year)`,
      group: "year",
      sortOrder: 0,
    });
    options.push({
      value: JSON.stringify({ type: "year", year: lastYear }),
      label: `📅 ${lastYear} (Full Year)`,
      group: "year",
      sortOrder: 1,
    });
    
    // Add custom period: Sept-Dec 2025 (for bonus calculations)
    options.push({
      value: JSON.stringify({ type: "custom", custom: { startYear: 2025, startMonth: 8, endYear: 2025, endMonth: 11 } }),
      label: `⭐ Sept-Dec 2025 (Bonus Period)`,
      group: "custom",
      sortOrder: 2,
    });
    
    // Add quarterly options for current year
    for (let q = 4; q >= 1; q--) {
      options.push({
        value: JSON.stringify({ type: "quarter", year: currentYear, quarter: q }),
        label: `Q${q} ${currentYear}`,
        group: "quarter-current",
        sortOrder: 10 + (4 - q),
      });
    }
    
    // Add quarterly options for last year
    for (let q = 4; q >= 1; q--) {
      options.push({
        value: JSON.stringify({ type: "quarter", year: lastYear, quarter: q }),
        label: `Q${q} ${lastYear}`,
        group: "quarter-last",
        sortOrder: 20 + (4 - q),
      });
    }
    
    // Generate months from 6 months ago to 12 months ahead (extended to include Sept 2025)
    for (let i = -6; i <= 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
      options.push({
        value: JSON.stringify({ type: "month", year: date.getFullYear(), month: date.getMonth() }),
        label: date.toLocaleString("default", { month: "long", year: "numeric" }),
        group: "month",
        sortOrder: 100 - i, // Most recent months first
      });
    }
    
    return options;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }


  // Get current period label for display
  const getPeriodLabel = () => {
    const { type, year, month, quarter, custom } = selectedPeriod;
    if (type === "custom" && custom) {
      const startDate = new Date(custom.startYear, custom.startMonth, 1);
      const endDate = new Date(custom.endYear, custom.endMonth, 1);
      return `${startDate.toLocaleString("default", { month: "short" })} - ${endDate.toLocaleString("default", { month: "short", year: "numeric" })}`;
    } else if (type === "month") {
      const date = new Date(year, month, 1);
      return date.toLocaleString("default", { month: "long", year: "numeric" });
    } else if (type === "quarter") {
      return `Q${quarter} ${year}`;
    } else if (type === "year") {
      return `${year} (Full Year)`;
    }
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Period:</label>
          <select
            value={JSON.stringify(selectedPeriod)}
            onChange={(e) => setSelectedPeriod(JSON.parse(e.target.value))}
            className="px-3 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]">
            {getPeriodOptions().map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={localFilters.toInvoice}
            onChange={(e) => setLocalFilters({ ...localFilters, toInvoice: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span>Show only "to invoice"</span>
        </label>
        <button
          onClick={() => setLocalFilters({ ...localFilters, hideNonBillable: !localFilters.hideNonBillable })}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            localFilters.hideNonBillable
              ? "bg-gray-200 text-gray-700 hover:bg-gray-300"
              : "bg-blue-500 text-white hover:bg-blue-600"
          }`}
        >
          {localFilters.hideNonBillable ? "Show Non-billable" : "Hide Non-billable"}
        </button>
        <button
          onClick={exportToCSV}
          disabled={!profitabilityData || !profitabilityData.personProfitability || profitabilityData.personProfitability.length === 0}
          className="ml-auto px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2">
          <FaDownload />
          Export CSV
        </button>
      </div>

      {/* Profitability Table */}
      {profitabilityData && profitabilityData.personProfitability.length > 0 ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Person</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost €</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">TJM Sell €</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">TJM Overwritten €</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Days Worked</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Invoicable €</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Invoiced €</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" title="Benefit based on invoiced revenue if available, otherwise invoicable">
                    Benefit € (Actual)
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" title="Margin based on invoiced revenue if available, otherwise invoicable">
                    Margin % (Actual)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {profitabilityData.personProfitability.map((person) => {
                  // Filter projects based on "to invoice" filter
                  const filteredProjects = localFilters.toInvoice
                    ? person.projectBreakdown.filter((project) => {
                        const isNotInvoiced = project.calculatedRevenue > 0 && (!project.invoicedRevenue || project.invoicedRevenue === 0);
                        return isNotInvoiced;
                      })
                    : person.projectBreakdown;

                  // Skip person if no projects match the filter
                  if (filteredProjects.length === 0) return null;

                  // Calculate person totals based on filtered projects
                  const personTotalCost = filteredProjects.reduce((sum, project) => sum + project.allocatedCost, 0);
                  const personTotalDays = filteredProjects.reduce((sum, project) => sum + project.daysWorked, 0);
                  const personTotalInvoicable = filteredProjects.reduce((sum, project) => sum + project.calculatedRevenue, 0);
                  const personTotalInvoiced = filteredProjects.reduce((sum, project) => sum + project.invoicedRevenue, 0);
                  const personTotalBenefit = filteredProjects.reduce((sum, project) => sum + project.benefit, 0);
                  const personTotalMargin = personTotalInvoicable > 0 ? (personTotalBenefit / personTotalInvoicable) * 100 : 0;
                  
                  return (
                    <React.Fragment key={person.userId}>
                      {/* Person Total Row */}
                      <tr className="bg-blue-50 border-t-2 border-blue-200">
                        <td className="px-4 py-3 text-sm font-bold text-gray-900">{person.name}</td>
                        <td className="px-4 py-3 text-sm font-bold text-gray-700">TOTAL</td>
                        <td className="px-4 py-3 text-sm font-bold text-gray-600">-</td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">{Math.round(personTotalCost)}</td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-600">{Math.round(person.baseTjms || 0)}</td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-600">-</td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-700">{personTotalDays.toFixed(1)}</td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">{Math.round(personTotalInvoicable)}</td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">{Math.round(personTotalInvoiced)}</td>
                        <td className={`px-4 py-3 text-sm text-right font-bold ${personTotalBenefit >= 0 ? "text-green-600" : "text-red-600"}`}>
                          {Math.round(personTotalBenefit)}
                        </td>
                        <td className={`px-4 py-3 text-sm text-right font-bold ${
                          personTotalMargin >= 30 ? "text-green-600" : personTotalMargin >= 15 ? "text-yellow-600" : "text-red-600"
                        }`}>
                          {personTotalMargin.toFixed(1)}%
                        </td>
                      </tr>
                      {/* Person Project Rows */}
                      {filteredProjects.map((project, idx) => {
                        const isNotInvoiced = project.calculatedRevenue > 0 && (!project.invoicedRevenue || project.invoicedRevenue === 0);
                        return (
                        <tr key={`${person.userId}-${project.projectId}-${idx}`} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{person.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            <div className="flex items-center gap-2">
                              <span>{project.projectName}</span>
                              {isNotInvoiced && (
                                <div className="flex items-center gap-1">
                                  <span className="bg-yellow-100 text-yellow-700 border-yellow-200 border-[1px] uppercase text-xs px-2 py-1 rounded">
                                    to invoice
                                  </span>
                                  {project.projectId && (
                                    <Link
                                      to={`/project/${project.projectId}`}
                                      target="_blank"
                                      className="text-blue-600 hover:text-blue-800 underline text-xs"
                                      onClick={(e) => e.stopPropagation()}
                                      title="Go to project to create invoice"
                                    >
                                      ↗
                                    </Link>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 uppercase">{project.projectType || "-"}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900">{Math.round(project.allocatedCost)}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-600">{Math.round(person.baseTjms || 0)}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-600">{Math.round(project.overwrittenTjms ?? person.baseTjms ?? 0)}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-700">{project.daysWorked.toFixed(1)}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900">{Math.round(project.calculatedRevenue)}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900">
                            <div className="flex items-center justify-end gap-2">
                              <span>{Math.round(project.invoicedRevenue || 0)}</span>
                              {project.invoiceIds && project.invoiceIds.length > 0 && (
                                <Link
                                  to={`/invoice/${project.invoiceIds[0]}`}
                                  target="_blank"
                                  className="text-blue-600 hover:text-blue-800 underline text-xs"
                                  onClick={(e) => e.stopPropagation()}
                                  title={project.invoiceIds.length > 1 ? `View invoice (${project.invoiceIds.length} invoices)` : "View invoice"}
                                >
                                  ↗
                                </Link>
                              )}
                            </div>
                          </td>
                          <td className={`px-4 py-3 text-sm text-right font-medium ${project.benefit >= 0 ? "text-green-600" : "text-red-600"}`} title={project.invoicedRevenue > 0 ? `Based on invoiced: ${Math.round(project.benefitFromInvoiced)} | Based on invoicable: ${Math.round(project.benefitFromInvoicable)}` : `Based on invoicable: ${Math.round(project.benefitFromInvoicable)}`}>
                            {Math.round(project.benefit)}
                          </td>
                          <td className={`px-4 py-3 text-sm text-right font-medium ${
                            project.margin >= 30 ? "text-green-600" : project.margin >= 15 ? "text-yellow-600" : "text-red-600"
                          }`} title={project.invoicedRevenue > 0 ? `Margin from invoiced: ${project.marginFromInvoiced.toFixed(1)}% | Margin from invoicable: ${project.marginFromInvoicable.toFixed(1)}%` : `Margin from invoicable: ${project.marginFromInvoicable.toFixed(1)}%`}>
                            {project.margin.toFixed(1)}%
                          </td>
                        </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                <tr>
                  <td className="px-4 py-3 text-sm font-bold text-gray-900" colSpan="3">
                    TOTAL
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">
                    {(() => {
                      const filteredData = profitabilityData.personProfitability.map((person) => {
                        const filteredProjects = localFilters.toInvoice
                          ? person.projectBreakdown.filter((project) => {
                              const isNotInvoiced = project.calculatedRevenue > 0 && (!project.invoicedRevenue || project.invoicedRevenue === 0);
                              return isNotInvoiced;
                            })
                          : person.projectBreakdown;
                        return { ...person, projectBreakdown: filteredProjects };
                      }).filter((person) => person.projectBreakdown.length > 0);
                      
                      return filteredData.reduce((sum, person) => 
                        sum + person.projectBreakdown.reduce((pSum, project) => pSum + project.allocatedCost, 0), 0
                      ).toFixed(0);
                    })()}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-bold text-gray-600">-</td>
                  <td className="px-4 py-3 text-sm text-right font-bold text-gray-600">-</td>
                  <td className="px-4 py-3 text-sm text-right font-bold text-gray-700">
                    {(() => {
                      const filteredData = profitabilityData.personProfitability.map((person) => {
                        const filteredProjects = localFilters.toInvoice
                          ? person.projectBreakdown.filter((project) => {
                              const isNotInvoiced = project.calculatedRevenue > 0 && (!project.invoicedRevenue || project.invoicedRevenue === 0);
                              return isNotInvoiced;
                            })
                          : person.projectBreakdown;
                        return { ...person, projectBreakdown: filteredProjects };
                      }).filter((person) => person.projectBreakdown.length > 0);
                      
                      return filteredData.reduce((sum, person) => 
                        sum + person.projectBreakdown.reduce((pSum, project) => pSum + project.daysWorked, 0), 0
                      ).toFixed(1);
                    })()}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">
                    {(() => {
                      const filteredData = profitabilityData.personProfitability.map((person) => {
                        const filteredProjects = localFilters.toInvoice
                          ? person.projectBreakdown.filter((project) => {
                              const isNotInvoiced = project.calculatedRevenue > 0 && (!project.invoicedRevenue || project.invoicedRevenue === 0);
                              return isNotInvoiced;
                            })
                          : person.projectBreakdown;
                        return { ...person, projectBreakdown: filteredProjects };
                      }).filter((person) => person.projectBreakdown.length > 0);
                      
                      return filteredData.reduce((sum, person) => 
                        sum + person.projectBreakdown.reduce((pSum, project) => pSum + project.calculatedRevenue, 0), 0
                      ).toFixed(0);
                    })()}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">
                    {(() => {
                      const filteredData = profitabilityData.personProfitability.map((person) => {
                        const filteredProjects = localFilters.toInvoice
                          ? person.projectBreakdown.filter((project) => {
                              const isNotInvoiced = project.calculatedRevenue > 0 && (!project.invoicedRevenue || project.invoicedRevenue === 0);
                              return isNotInvoiced;
                            })
                          : person.projectBreakdown;
                        return { ...person, projectBreakdown: filteredProjects };
                      }).filter((person) => person.projectBreakdown.length > 0);
                      
                      return filteredData.reduce((sum, person) => 
                        sum + person.projectBreakdown.reduce((pSum, project) => pSum + project.invoicedRevenue, 0), 0
                      ).toFixed(0);
                    })()}
                  </td>
                  <td className={`px-4 py-3 text-sm text-right font-bold ${
                    (() => {
                      const filteredData = profitabilityData.personProfitability.map((person) => {
                        const filteredProjects = localFilters.toInvoice
                          ? person.projectBreakdown.filter((project) => {
                              const isNotInvoiced = project.calculatedRevenue > 0 && (!project.invoicedRevenue || project.invoicedRevenue === 0);
                              return isNotInvoiced;
                            })
                          : person.projectBreakdown;
                        return { ...person, projectBreakdown: filteredProjects };
                      }).filter((person) => person.projectBreakdown.length > 0);
                      
                      const totalBenefit = filteredData.reduce((sum, person) => 
                        sum + person.projectBreakdown.reduce((pSum, project) => pSum + project.benefit, 0), 0
                      );
                      return totalBenefit >= 0 ? "text-green-600" : "text-red-600";
                    })()
                  }`}>
                    {(() => {
                      const filteredData = profitabilityData.personProfitability.map((person) => {
                        const filteredProjects = localFilters.toInvoice
                          ? person.projectBreakdown.filter((project) => {
                              const isNotInvoiced = project.calculatedRevenue > 0 && (!project.invoicedRevenue || project.invoicedRevenue === 0);
                              return isNotInvoiced;
                            })
                          : person.projectBreakdown;
                        return { ...person, projectBreakdown: filteredProjects };
                      }).filter((person) => person.projectBreakdown.length > 0);
                      
                      return filteredData.reduce((sum, person) => 
                        sum + person.projectBreakdown.reduce((pSum, project) => pSum + project.benefit, 0), 0
                      ).toFixed(0);
                    })()}
                  </td>
                  <td className={`px-4 py-3 text-sm text-right font-bold ${
                    (() => {
                      const filteredData = profitabilityData.personProfitability.map((person) => {
                        const filteredProjects = localFilters.toInvoice
                          ? person.projectBreakdown.filter((project) => {
                              const isNotInvoiced = project.calculatedRevenue > 0 && (!project.invoicedRevenue || project.invoicedRevenue === 0);
                              return isNotInvoiced;
                            })
                          : person.projectBreakdown;
                        return { ...person, projectBreakdown: filteredProjects };
                      }).filter((person) => person.projectBreakdown.length > 0);
                      
                      const totalRevenue = filteredData.reduce((sum, person) => 
                        sum + person.projectBreakdown.reduce((pSum, project) => pSum + project.calculatedRevenue, 0), 0
                      );
                      const totalBenefit = filteredData.reduce((sum, person) => 
                        sum + person.projectBreakdown.reduce((pSum, project) => pSum + project.benefit, 0), 0
                      );
                      const totalMargin = totalRevenue > 0 ? (totalBenefit / totalRevenue) * 100 : 0;
                      return totalMargin >= 30 ? "text-green-600" : totalMargin >= 15 ? "text-yellow-600" : "text-red-600";
                    })()
                  }`}>
                    {(() => {
                      const filteredData = profitabilityData.personProfitability.map((person) => {
                        const filteredProjects = localFilters.toInvoice
                          ? person.projectBreakdown.filter((project) => {
                              const isNotInvoiced = project.calculatedRevenue > 0 && (!project.invoicedRevenue || project.invoicedRevenue === 0);
                              return isNotInvoiced;
                            })
                          : person.projectBreakdown;
                        return { ...person, projectBreakdown: filteredProjects };
                      }).filter((person) => person.projectBreakdown.length > 0);
                      
                      const totalRevenue = filteredData.reduce((sum, person) => 
                        sum + person.projectBreakdown.reduce((pSum, project) => pSum + project.calculatedRevenue, 0), 0
                      );
                      const totalBenefit = filteredData.reduce((sum, person) => 
                        sum + person.projectBreakdown.reduce((pSum, project) => pSum + project.benefit, 0), 0
                      );
                      const totalMargin = totalRevenue > 0 ? (totalBenefit / totalRevenue) * 100 : 0;
                      return totalMargin.toFixed(1);
                    })()}%
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center p-8 text-gray-500">No profitability data found for the selected period</div>
      )}

    </div>
  );
}
